import { useEffect, useState } from 'react'
import { useControls, useTransformContext } from 'react-zoom-pan-pinch'
import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import { useAssignedDigitalTrays } from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays.context'
import { useNavigate, useParams } from 'react-router-dom'
import {
  SubTrayItemProductDetails,
  TrayImageRecord,
} from 'lib/services/api/product-service/receiveItems/types'
import { getLatestTrayImage } from 'lib/utils/getLatestTrayImage'
import dayjs from 'lib/dayjs'
import useDTMS3Image from 'lib/utils/useDTMS3Image'

const useTrayProcedureEditModeLogic = ({
  handleConfirm,
  setModalOpen,
  trayDetails,
}: {
  handleConfirm: () => void
  setModalOpen: (open: boolean) => void
  trayDetails: SubTrayItemProductDetails | undefined
}) => {
  const navigate = useNavigate()
  const { surgeryId } = useParams()

  const { zoomIn, zoomOut, setTransform, resetTransform } = useControls()
  const transformContext = useTransformContext()

  const { selectedScrewIndices } = useSPD()
  const {
    hasNurseCapturedTrayImage,
    selectedSubTray,
    nurseUsageHistory,
    usageScrews,
    usagePlates,
    usageOtherItems,
    usageCustomImplants,
  } = useAssignedDigitalTrays()

  const [isCancelConfirmationModalOpen, setIsCancelConfirmationModalOpen] =
    useState(false)
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState<boolean>(false)
  const [isReferenceOpen, setIsReferenceOpen] = useState<boolean>(false)
  const [imageScale, setImageScale] = useState<number>(1)
  const [imageRotation, setImageRotation] = useState<number>(0)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [isLegendOpen, setIsLegendOpen] = useState<boolean>(false)
  const [showImageVerificationModal, setShowImageVerificationModal] =
    useState<boolean>(false)

  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0)
  const [imageHistory, setImageHistory] = useState<TrayImageRecord[]>([])
  const [isViewingHistory, setIsViewingHistory] = useState<boolean>(false)
  const [areControlsVisible, setAreControlsVisible] = useState<boolean>(false)

  const hasConfirmedHardware =
    usageScrews.length > 0 ||
    usagePlates.length > 0 ||
    usageOtherItems.length > 0 ||
    usageCustomImplants.length > 0
  const selectedScrewsCount = selectedScrewIndices.length
  const hasNurseCapturedEndOfCaseReferenceImage = hasNurseCapturedTrayImage
  const shouldMainButtonBeDisabled =
    selectedScrewsCount === 0 && !hasConfirmedHardware
  const trayID = selectedSubTray?.id
  const screwsRemovedByNurse = surgeryId
    ? trayDetails?.removedByNurse?.[surgeryId]
    : []

  // Only show navigation controls if we actually have multiple images
  const hasMultipleImages = imageHistory.length > 1
  // Get the current image to display
  const currentImage = imageHistory[currentImageIndex]
  // Format the timestamp only if we have a real timestamp (not for legacy images)
  const formattedTimestamp =
    currentImage?.timestamp && !trayDetails?.trayImageHistory?.records
      ? '' // Don't show timestamp for legacy images
      : currentImage?.timestamp
      ? dayjs(currentImage.timestamp).format('MMM D, YYYY h:mm A')
      : ''

  // Get the raw tray image URL
  const rawTrayImageUrl =
    currentImage?.imageUrl || getLatestTrayImage(trayDetails, ['rep', 'spd'])

  // Use our custom hook to get a presigned URL for the image
  const { imageUrl: trayImage, isLoading: isImageLoading } = useDTMS3Image({
    url: rawTrayImageUrl,
    defaultImage: '',
  })

  const getReferenceImageTitle = () => {
    if (!currentImage) return 'Reference Image'

    if (currentImage.addedBy.role === 'nurse') {
      return 'End of Case Reference Image'
    } else if (currentImage.addedBy.role === 'rep') {
      return 'Reference Image (Captured in Vendor Distribution Center)'
    } else {
      return 'Reference Image (Captured in Vendor Distribution Center)'
    }
  }
  const referenceImageTitle = getReferenceImageTitle()

  useEffect(() => {
    resetTransform()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case '=':
          case '+':
            e.preventDefault()
            zoomIn(0.5)
            break
          case '-':
            e.preventDefault()
            zoomOut(0.5)
            break
          case '0':
            e.preventDefault()
            resetTransform()
            break
          // Arrow keys for panning
          case 'ArrowUp':
            e.preventDefault()
            handlePan('up')
            break
          case 'ArrowDown':
            e.preventDefault()
            handlePan('down')
            break
          case 'ArrowLeft':
            e.preventDefault()
            handlePan('left')
            break
          case 'ArrowRight':
            e.preventDefault()
            handlePan('right')
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyboard)
    return () => window.removeEventListener('keydown', handleKeyboard)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (trayDetails?.trayImageHistory?.records) {
      // First sort all records by timestamp (newest first)
      const sortedRecords = [...trayDetails.trayImageHistory.records].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )

      // Filter the records according to the new requirements
      const filteredRecords = filterImageHistory(sortedRecords)

      setImageHistory(filteredRecords)
    } else if (trayDetails?.trayImg) {
      // Create a single record for legacy trayImg
      setImageHistory([
        {
          imageUrl: trayDetails.trayImg,
          addedBy: { email: 'Unknown', role: 'spd' },
          timestamp: new Date(), // Since we don't have a timestamp for legacy images
        },
      ])
    } else {
      setImageHistory([])
    }
    // Reset to the latest image when tray details change
    setCurrentImageIndex(0)
    setIsViewingHistory(false)
  }, [trayDetails])

  // New function to filter image history according to requirements
  const filterImageHistory = (
    records: TrayImageRecord[]
  ): TrayImageRecord[] => {
    if (!records.length) return []

    // Find the latest Rep/SPD image
    const latestNonNurseImage = records.find(
      (record) => record.addedBy.role === 'rep' || record.addedBy.role === 'spd'
    )

    // Find the latest nurse image
    const latestNurseImage = records.find(
      (record) => record.addedBy.role === 'nurse'
    )

    const result: TrayImageRecord[] = []

    // Add the latest nurse image if it exists AND it's newer than the latest Rep/SPD image
    if (latestNurseImage) {
      if (
        !latestNonNurseImage ||
        new Date(latestNurseImage.timestamp).getTime() >
          new Date(latestNonNurseImage.timestamp).getTime()
      ) {
        result.push(latestNurseImage)
      }
    }

    // Add the latest Rep/SPD image if it exists
    if (latestNonNurseImage) {
      result.push(latestNonNurseImage)
    }

    // Sort the result by timestamp (newest first)
    return result.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )
  }

  const goToPreviousImage = () => {
    if (currentImageIndex < imageHistory.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1)
      setIsViewingHistory(true)
    }
  }

  const goToNextImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1)
      setIsViewingHistory(currentImageIndex - 1 !== 0)
    }
  }

  const goToLatestImage = () => {
    setCurrentImageIndex(0)
    setIsViewingHistory(false)
  }

  const handleCancel = () => {
    navigate('../assigned-digital-trays', { replace: true })
  }

  const handlePan = (direction: 'up' | 'down' | 'left' | 'right') => {
    if (transformContext.mounted) {
      const panStep = 130
      const {
        positionX,
        positionY,
        previousScale: scale,
      } = transformContext.getContext().state

      switch (direction) {
        case 'up':
          setTransform(positionX, positionY - panStep, scale)
          break
        case 'down':
          setTransform(positionX, positionY + panStep, scale)
          break
        case 'left':
          setTransform(positionX - panStep, positionY, scale)
          break
        case 'right':
          setTransform(positionX + panStep, positionY, scale)
          break
      }
    }
  }

  const handleZoomIn = () => {
    setImageScale((prev) => Math.min(prev + 0.5, 3))
  }

  const handleZoomOut = () => {
    setImageScale((prev) => Math.max(prev - 0.5, 0.5))
  }

  const handleRotateImage = () => {
    setImageRotation((prev) => (prev + 90) % 360)
  }

  const handleMainButtonClick = () => {
    if (selectedScrewsCount > 0) {
      setModalOpen(true)
    } else if (!isLatestImageByNurse) {
      // If the latest image is not by a nurse, force recapture
      handleRecaptureReferenceImage()
    } else if (!hasNurseCapturedEndOfCaseReferenceImage) {
      // If the latest image is by a nurse but not from current session, ask for verification
      setShowImageVerificationModal(true)
    } else {
      // If the latest image is by a nurse and from current session, proceed
      handleConfirm()
    }
  }

  const handleRecaptureReferenceImage = () => {
    navigate(`../capture-tray/${trayID}?mode=image-only&return=visualization`)
  }

  const toggleControlsVisibility = () => {
    setAreControlsVisible(!areControlsVisible)
  }

  // Check if the latest image was captured by a nurse
  const isLatestImageByNurse =
    imageHistory.length > 0 && imageHistory[0]?.addedBy?.role === 'nurse'

  return {
    handlePan,
    zoomIn,
    zoomOut,
    resetTransform,
    handleZoomIn,
    handleZoomOut,
    isReferenceOpen,
    setIsReferenceOpen,
    imageScale,
    imageRotation,
    handleRotateImage,
    isImagePreviewOpen,
    setIsImagePreviewOpen,
    selectedScrewsCount,
    referenceImageTitle,
    trayID,
    hasNurseCapturedEndOfCaseReferenceImage,
    isCancelConfirmationModalOpen,
    setIsCancelConfirmationModalOpen,
    hasConfirmedHardware,
    handleCancel,
    showCancelModal,
    setShowCancelModal,
    handleMainButtonClick,
    shouldMainButtonBeDisabled,
    handleRecaptureReferenceImage,
    trayImage,
    isImageLoading,
    isLegendOpen,
    setIsLegendOpen,
    screwsRemovedByNurse,
    formattedTimestamp,
    hasMultipleImages,
    isViewingHistory,
    isViewingLatestImage: currentImageIndex === 0,
    goToPreviousImage,
    goToNextImage,
    goToLatestImage,
    currentImageIndex,
    totalImages: imageHistory.length,
    nurseUsageHistory,
    areControlsVisible,
    toggleControlsVisibility,
    isLatestImageByNurse,
    showImageVerificationModal,
    setShowImageVerificationModal,
  }
}

export default useTrayProcedureEditModeLogic
